/**
 * EmailActivityTab — admin/staff view of every email ever sent to a user.
 *
 * Used in:
 *   • pages/admin/ParentDetail.js   (under "Activity" tab)
 *   • pages/admin/CustomerDetail.js (under "Activity" tab)
 *
 * iter252 — backed by `GET /api/admin/users/{user_id}/email-activity`
 * and `/api/admin/email-activity/{id}/view` for the new-tab body render.
 * Live delivery status (delivered/opened/clicked/bounced) flows in from
 * the existing Resend webhook stream.
 */
import { useEffect, useState } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Loader2, Search, ExternalLink, Mail, Megaphone, Filter, Send } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  sent: { label: 'Sent', class: 'bg-slate-100 text-slate-700' },
  failed: { label: 'Failed', class: 'bg-rose-100 text-rose-700' },
  delivered: { label: 'Delivered', class: 'bg-emerald-100 text-emerald-700' },
  opened: { label: 'Opened', class: 'bg-blue-100 text-blue-700' },
  clicked: { label: 'Clicked', class: 'bg-indigo-100 text-indigo-700' },
  bounced: { label: 'Bounced', class: 'bg-amber-100 text-amber-700' },
  complained: { label: 'Complained', class: 'bg-rose-200 text-rose-800' },
};

export function EmailActivityTab({ userId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('all');
  const [pageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        skip: String(pageIndex * pageSize),
      });
      if (kind !== 'all') params.set('kind', kind);
      if (search.trim()) params.set('search', search.trim());
      const { data } = await api.get(`/admin/users/${userId}/email-activity?${params.toString()}`);
      setRows(data.activity || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load email activity');
    } finally { setLoading(false); }
  };

  useEffect(() => { setPageIndex(0); /* eslint-disable-next-line */ }, [search, kind]);
  useEffect(() => {
    if (!userId) return;
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [userId, search, kind, pageIndex]);

  const openEmail = async (activityId) => {
    // Pull the token off the same localStorage slot the api service uses
    // — the HTML view endpoint requires auth, and a plain window.open()
    // can't attach Authorization headers. We fetch + objectURL instead.
    try {
      const { data } = await api.get(`/admin/email-activity/${activityId}/view`, {
        responseType: 'text',
      });
      const blob = new Blob([data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after the browser has had a moment to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load email body');
    }
  };

  // iter253 — re-send the captured email body to the same recipient
  // (e.g. parent says "I never got it"). Confirm first because in rare
  // cases the original action context has changed (e.g. price update).
  const [resendingId, setResendingId] = useState(null);
  const resendEmail = async (row) => {
    if (!window.confirm(
      `Resend this email to ${row.email}?\n\nSubject: ${row.subject || '(no subject)'}\n\n` +
      `This will send the same captured body — it does NOT re-trigger the source action ` +
      `(receipt, refund, etc.), it just delivers a copy.`
    )) return;
    setResendingId(row.id);
    try {
      const { data } = await api.post(`/admin/email-activity/${row.id}/resend`);
      toast.success(`Resent to ${data.sent_to}`);
      // Reload so the new row (and the original) refresh.
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resend email');
    } finally { setResendingId(null); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Card data-testid="email-activity-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Email Activity
          <span className="text-xs font-normal text-slate-500">
            ({total.toLocaleString()} email{total === 1 ? '' : 's'} sent)
          </span>
          {loading && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              className="pl-8 h-9"
              placeholder="Filter by subject…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="email-activity-search"
            />
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Filter className="h-3 w-3 text-slate-400" />
            {[
              { v: 'all', label: 'All' },
              { v: 'transactional', label: 'Transactional' },
              { v: 'marketing', label: 'Marketing' },
            ].map((opt) => (
              <Badge
                key={opt.v}
                variant={kind === opt.v ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setKind(opt.v)}
                data-testid={`email-activity-kind-${opt.v}`}
              >{opt.label}</Badge>
            ))}
          </div>
        </div>

        {rows.length === 0 && !loading && (
          <div className="text-center text-sm text-slate-400 py-8" data-testid="email-activity-empty">
            {total === 0 && !search && kind === 'all'
              ? 'No emails sent to this user yet.'
              : 'No matching emails on this page.'}
          </div>
        )}

        {rows.length > 0 && (
          <div className="space-y-1" data-testid="email-activity-list">
            {rows.map((r) => {
              const s = STATUS_STYLES[r.status] || STATUS_STYLES.sent;
              const Icon = r.kind === 'marketing' ? Megaphone : Mail;
              return (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-3 p-2 border rounded-md hover:bg-slate-50 text-sm"
                  data-testid={`email-activity-row-${r.id}`}
                >
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${r.kind === 'marketing' ? 'text-indigo-500' : 'text-slate-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" title={r.subject}>{r.subject || '(no subject)'}</p>
                      <p className="text-xs text-slate-500">
                        {r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}
                        {r.source && r.source !== 'system' && ` · ${r.source}`}
                        {r.sent_by_name && ` · by ${r.sent_by_name}`}
                        {r.opened_at && ` · opened ${new Date(r.opened_at).toLocaleString()}`}
                        {r.clicked_at && ` · clicked ${new Date(r.clicked_at).toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                  <Badge className={s.class}>{s.label}</Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEmail(r.id)}
                    data-testid={`email-activity-view-${r.id}`}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resendEmail(r)}
                    disabled={resendingId === r.id}
                    className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                    title={`Re-send this email to ${r.email}`}
                    data-testid={`email-activity-resend-${r.id}`}
                  >
                    {resendingId === r.id
                      ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      : <Send className="h-3 w-3 mr-1" />}
                    Resend
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {total > pageSize && (
          <div className="flex items-center justify-between mt-3 text-xs text-slate-500" data-testid="email-activity-pagination">
            <span>Showing {pageIndex * pageSize + 1}–{Math.min((pageIndex + 1) * pageSize, total)} of {total}</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={pageIndex === 0 || loading} onClick={() => setPageIndex((p) => Math.max(0, p - 1))} data-testid="email-activity-prev">Prev</Button>
              <span>Page {pageIndex + 1} of {totalPages}</span>
              <Button size="sm" variant="outline" disabled={(pageIndex + 1) >= totalPages || loading} onClick={() => setPageIndex((p) => p + 1)} data-testid="email-activity-next">Next</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
